<template>

	<div class="game-info">
		<div class="" :class="gridClass">
			<div v-if="showAboutSection" class="game-info__section" :class="gridItemClass">

				<h3 class="h4 game-info__title">About this game</h3>
				<div
					v-html="game.gameDescription"
					class="game-info__description fc-pal--grey"
				/>
				<a class="link--action link--arrow link--colored game-info__download" v-if="$root.userHasPermission({
					'igt': ['AM', 'CM', 'SA'],
					'operator': ['customer', 'performance'],
				}) && game.assets.downloadableGameinformation && game.assets.downloadableGameinformation.length > 0"
					 :href="game.assets.downloadableGameinformation[0].link" target="_blank">Download Game Info</a>

			</div>
			<div class="game-info__section" :class="gridItemClass">

				<h3 class="h4 game-info__title">Information</h3>
				<div class="game-info__table fc-pal--grey">
					<div class="game-info__table-key">3rd Party Licensed</div>
					<div class="game-info__table-spacer"></div>
					<div class="game-info__table-value">
						<template v-if="game.thirdPartyLicenced">Yes</template>
						<template v-else>No</template>
					</div>
				</div>
				<div class="game-info__table fc-pal--grey">
					<div class="game-info__table-key">Number of Paylines</div>
					<div class="game-info__table-spacer"></div>
					<div class="game-info__table-value">{{ game.paylines }}</div>
				</div>
				<div class="game-info__table fc-pal--grey">
					<div class="game-info__table-key">Min/Max Stakes</div>
					<div class="game-info__table-spacer"></div>
					<div class="game-info__table-value">{{ game.minStakes }}/{{ game.maxStakes }}</div>
				</div>
				<div v-if="game.features !== 'feature-none'" class="game-info__table fc-pal--grey">
					<div class="game-info__table-key">Features</div>
					<div class="game-info__table-spacer"></div>
					<div class="game-info__table-value">{{ featureLabels([game.features]).join(', ') }}</div>
				</div>
				<div v-if="game.theme !== 'theme-none'" class="game-info__table fc-pal--grey">
					<div class="game-info__table-key">Theme</div>
					<div class="game-info__table-spacer"></div>
					<div class="game-info__table-value">{{ gameThemes }}</div>
				</div>

			</div>
			<div
				v-if="game.volatilityRating && game.volatilityRating !== 'none'"
				:class="gridItemClass"
				class="game-info__section"
			>
				<h3 class="h4 game-info__title">Game volatility</h3>
				<volatility-rating :volatility-slug="game.volatilityRating" :show-description="true"></volatility-rating>
			</div>
			<div class="game-info__section" :class="gridItemClass">
				<h3 class="h4 game-info__title">Supported Languages</h3>
				<div class="game-info__languages fc-pal--grey">{{ gameLanguages }}</div>
			</div>
			<div
				:class="gridItemClass"
				v-if="game.softwareId && game.softwareId.length > 0 && game.softwareId[0]"
				class="game-info__section"
			>
				<h3 class="h4 game-info__title">Software ID &amp; RTP</h3>
				<ul class="list--unstyled game-info__software fc-pal--grey">
					<li class="game-info__software-item" v-for="(item, index) in game.softwareId">
						{{ game.softwareId[index] }} - {{ game.percentages[index] }}%
						<template v-if="game.labels[index]"> - {{ game.labels[index] }}</template>
					</li>
				</ul>

			</div>
		</div>
	</div>

</template>

<script>
import { mapGetters } from 'vuex'

import VolatilityRating from '../molecules/VolatilityRating'

export default {
	name: 'GameDetail',
	components: {
		VolatilityRating
	},
	data () {
		return {}
	},
	props: {
		game: Object,
		gridClass: {
			type: String,
			default: 'grid grid--g80 grid--s60--phab grid--s72--not-phab'
			// <attr class="grid grid--g80 grid--s60--phab grid--s72--not-phab" />
		},
		gridItemClass: {
			type: String,
			default: 'grid__item one-half--ltab one-third--desk'
			// <attr class="grid__item one-half--ltab one-third--desk" />
		},
		showAboutSection: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		...mapGetters({
			'languageLabels': 'app/languageLabels',
			'featureLabels': 'app/featureLabels',
			'themeLabels': 'app/themeLabels',
		}),
		gameThemes() {
			return this.game.theme.map(a => a.name).join(', ')
		},
		gameLanguages() {
			return this.languageLabels(this.game.languages).join(', ')
		}
	}
}
</script>

<style lang="scss">

@import "src/scss/vars/attract";
@import "src/scss/toolkit";

$c: ".game-info";
#{$c} {
	font-size: 14px;

	.volatility-rating {
		@include media(phab) {
			display: block;
		}

		&__container {
			@include media(phab) {
				width: 100%;
			}
		}
	}

	&__section {
		&:nth-child(2), &:nth-child(3), &:nth-child(4), &:nth-child(5) {
			&:before {
				content: "";
				display: block;
				position: relative;
				top: -32px;
				border-bottom: 1px solid palette(lightest-grey);

				@include media(phab) {
					border-bottom-color: palette('divider');
					top: -24px;
				}
			}
		}

		&:nth-child(2) {
			&:before {
				@include media(not-phab) {
					display: none;
				}
			}
		}

		&:nth-child(3) {
			&:before {
				@include media(desk) {
					display: none;
				}
			}
		}
	}

	&__title {
		margin-bottom: rem(24);
	}

	&__download {
		display: inline-block;
		margin-top: rem(32);
	}

	&__table {
		@include extend(grid);
		@include extend(grid--b0);
		@include extend(grid--bottom);
		@include extend(grid--auto);
		@include extend(grid--no-wrap);
		margin-bottom: 1em;

		&-key,
		&-spacer,
		&-value {
			@include extend(grid__item);
		}

		&-key {
			padding-right: 5px;
		}

		&-spacer {
			flex-grow: 1;

			@include media(not-phab) {
				border-bottom: 1px dotted;
			}
		}

		&-value {
			@include extend(font--bold);
			padding-left: 5px;
		}
	}

	&__software {
		margin-bottom: -1em;

		&-item {
			margin-bottom: 1em;
		}
	}
}

</style>



// WEBPACK FOOTER //
// src/components/molecules/GameInfo.vue